import { useIsEmbeddedWebView } from '@hungryroot/environment';
import {
  useExperimentFlags,
  type ExperimentHookOptions,
} from '@hungryroot/experiments';
import { AuthState, useIdentify } from '@hungryroot/rest-hooks';
import {
  CYO_ROUTE,
  PRECHECKOUT_PLAN_ROUTE,
  ROUTE_QUIZ_MEAL_FREQUENCIES,
  ROUTE_SIGN_UP,
  ROUTE_QUIZ_BUILDING,
} from '@hungryroot/routes';

/** The experiments `useFunnelRedirects` enrolls, and can be told not to. */
type FunnelRedirectExperiment = 234 | 253 | 220;

function enrollOption(
  exp: FunnelRedirectExperiment,
  suppressed: readonly FunnelRedirectExperiment[]
): Pick<ExperimentHookOptions, 'enroll'> {
  return { enroll: !suppressed.includes(exp) };
}

/**
 * `suppressEnrollment` names the experiments this surface must NOT enroll
 * visitors in. Their reads still resolve from any existing enrollment — they
 * just never create one.
 *
 * Use it on surfaces that are not part of the funnel's enrollment path.
 * `/signup/` passes `[220]`: a visitor who skipped the quiz cannot have
 * experienced that treatment, and the value doesn't affect anything signup
 * reads (it uses only `afterSignup` and `afterQuiz`, which are
 * experiment-independent).
 *
 * Listing ids explicitly rather than taking a blanket `{ enroll: false }` keeps
 * the blast radius visible at the call site: suppressing enrollment shifts an
 * experiment's population, so it should never happen to an experiment by
 * accident just because it shares this hook. Exp 234 is deliberately absent
 * from `/signup/`'s list.
 *
 * Omitted entirely, every experiment enrolls as before — correct for `/quiz/*`
 * (the intended enrollment point) and `/limited-time/`.
 *
 * Meal frequencies is always the last quiz screen, so these enrollments no
 * longer change `beforeCyo` / `beforePlan`. The reads stay because they are
 * the enrollment point.
 */
export function useFunnelRedirects(options?: {
  suppressEnrollment?: readonly FunnelRedirectExperiment[];
}) {
  const suppressed = options?.suppressEnrollment ?? [];
  const customer = useIdentify();
  const isEmbeddedWebView = useIsEmbeddedWebView();
  const { isLoading: is234Loading } = useExperimentFlags(
    234,
    enrollOption(234, suppressed)
  );
  // Exp 253 is rolled out and nothing reads its flag; this call exists only
  // to keep enrolling quiz visitors while the backend still depends on the
  // row. See the definition in `@hungryroot/experiments` for the why.
  const { isLoading: is253Loading } = useExperimentFlags(
    253,
    enrollOption(253, suppressed)
  );
  const { isLoading: is220Loading } = useExperimentFlags(
    220,
    enrollOption(220, suppressed)
  );

  if (
    customer.authState === AuthState.isLoading ||
    is234Loading ||
    is253Loading ||
    is220Loading
  ) {
    return null;
  }

  const customerIsAuthenticated =
    customer.authState === AuthState.isAuthenticated;

  // After user is signed up, send to Plan page if it's a mobile webview, otherwise CYO
  const afterBuildingAfterQuizRedirect = isEmbeddedWebView
    ? PRECHECKOUT_PLAN_ROUTE
    : CYO_ROUTE;

  // Anonymous users see the Building screen post-quiz before signup;
  // authenticated users skip straight to CYO/Plan.
  const afterQuiz = customerIsAuthenticated
    ? afterBuildingAfterQuizRedirect
    : ROUTE_QUIZ_BUILDING;

  // After Building screen finishes, send the user to signup if they are logged out
  // Technically, with the above logic, a logged-in user can't get to the Building screen organically,
  // but this satisfies an e2e test that checks there isn't an infinite redirect and this helps solve the redirect issue
  // In the unlikely event that a logged-in user gets to the Building screen somehow
  const afterBuilding =
    customerIsAuthenticated === true
      ? afterBuildingAfterQuizRedirect
      : ROUTE_SIGN_UP;

  const lastQuizRoute = ROUTE_QUIZ_MEAL_FREQUENCIES;

  // If user clicks Back on Plan screen, go back to last quiz route if on mobile webview, otherwise CYO
  const beforePlan = isEmbeddedWebView ? lastQuizRoute : CYO_ROUTE;

  return {
    afterQuiz,
    afterBuilding,
    afterSignup: afterBuildingAfterQuizRedirect,
    afterCyo: PRECHECKOUT_PLAN_ROUTE,
    beforeCyo: lastQuizRoute,
    beforePlan,
  } as const;
}
